import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InsuranceCompanySeed } from '../../shared/models/domain/InsuranceCompanySeed';
import { Person } from '../../app-user-management/model/user-access-management.model';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { EcapQueryHandlerResponse, Filter, GqlWatchResponse } from '../../shared/models';
import { CommonService } from '../../shared/services/common.service';
import { GqlQueryBuilderService } from '../../shared/services/gql-query-builder.service';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import { GraphqlService } from '../../shared/services/graphql.service';
import { BasicInformationModel } from '../models/basic-information.model';
import { InspectorAnswer } from '../models/inspector-question-answer.model';
import {
    CaseReference,
    InsuranceCase,
    PolicyHolderDetails,
    ServiceAdditionalInfo
} from '../models/insurance-case.model';
import { ServiceModel } from '../models/service-list.model';
import { ComponentSeed } from '../../shared/models/domain/ComponentSeed';
import { ShellDomainProvider, UtilityService } from '../../shared/standalone-packages/ecap3/core';
import { phoneValidator } from '../../shared/services/phone-number-validator';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';
@Injectable({
    providedIn: 'root'
})
export class CaseDefaultsService {
    private readonly urlResolver = inject(UrlResolverService);
    inspectorAnswerForm: FormGroup;
    currentRole: string;
    dragableDrawerOpen = new BehaviorSubject<any>(null);
    clickedUpdateCase = new BehaviorSubject<boolean>(null);
    clickedUploadCaseSaveButton = new BehaviorSubject<boolean>(false);
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    constructor(
        private formBuilder: FormBuilder,
        private http: HttpClient,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private gqlQueryCommandService: GqlQueryCommandService,
        private graphqlService: GraphqlService,
        private commonService: CommonService,
        private utilityService: UtilityService
    ) {}

    createCaseForm(emailData?, InsuranceCompanyName?, caseDraftData?: InsuranceCase) {
        // ;
        const {
            CleimNumberInsurance = (caseDraftData && caseDraftData.InsuranceClaimNumber) || emailData.Subject,
            EmailConversationId = emailData.ItemId,
            InsuranceOfficerName = (caseDraftData && caseDraftData.InsuranceOfficerName) || '',
            InsuranceOfficerEmail = (caseDraftData && caseDraftData.InsuranceOfficerEmail) || '',
            OrderDate = (caseDraftData &&
                new Date(caseDraftData.OrderDate).getFullYear() >= 2000 &&
                caseDraftData.OrderDate) ||
                new Date(),
            IssuerZipCode = (caseDraftData && caseDraftData.ZipCodeOfIssuer) || '',
            PolicyHolderName,
            IssuerCompanyName,
            IssuerEmailAddress,
            IssuerPhoneNumber,
            CaseType,
            Remarks,
            ServiceName,
            Unit,
            Quantities,
            UnitPrice,
            TotalPrice,
            Vat,
            OfferNumber,
            Trade,
            Component,
            AssignedBackOffice,
            AssignedBackOfficeSearchText,
            AssignedInspector,
            AssignedInspectorSearchText,
            InvoiceType,
            CaseStatus = {
                Key: 'AUDIT_PHASE',
                Value: 'audit-phase'
            },
            BackofficeUserId = (caseDraftData && caseDraftData.BackofficeUserId) || '',
            InspectorUserId = (caseDraftData && caseDraftData.InspectorUserId) || '',
            UpdatedOfferNumber
        }: BasicInformationModel = new BasicInformationModel();
        const ServiceList: ServiceModel[] = ((caseDraftData && caseDraftData.CaseServiceList) || []).map(
            service => new ServiceModel(service, this.commonService.getCurrentRoleUser)
        );
        if (!(caseDraftData && caseDraftData.CaseServiceList)) {
            ServiceList.push(new ServiceModel());
        }
        const {
            InsuranceComapnayName = emailData.InsuranceCompanyName,
            InsuranceComapnayId,
            DepartmentId,
            DepartmentName
        }: CaseReference = new CaseReference();
        return this.formBuilder.group({
            CaseReferenceInfo: this.createCaseReferenceForm(emailData, InsuranceCompanyName, caseDraftData),
            InsuranceClaimNumber: [CleimNumberInsurance, [Validators.required]],
            InsuranceOfficerName: [InsuranceOfficerName],
            InsuranceOfficerEmail: [InsuranceOfficerEmail],
            OrderDate: [OrderDate, [Validators.required, Validators.maxLength(100)]],
            ZipCodeOfIssuer: [IssuerZipCode, [Validators.required, Validators.maxLength(25)]],
            BackofficeUserId: [BackofficeUserId, [Validators.required, Validators.maxLength(100)]],
            InspectorUserId: [InspectorUserId],
            InvoiceType: [InvoiceType],
            AssignedBackOfficeSearchText: [AssignedBackOfficeSearchText],
            AssignedInspectorSearchText: [AssignedInspectorSearchText],
            CaseStatus: [CaseStatus],
            EmailConversationId: [EmailConversationId],
            CaseServiceList: this.formBuilder.array(ServiceList.map(c => this.createServiceForm(c, caseDraftData))),
            PolicyHolderInfo: this.createPolicyHolderDetails(emailData, caseDraftData)
        });
    }

    createPolicyHolderDetails(emailData?, caseDraftData?: InsuranceCase) {
        const {
            Name = (caseDraftData && caseDraftData.PolicyHolderInfo && caseDraftData.PolicyHolderInfo.Name) || '',
            CompanyName = (caseDraftData &&
                caseDraftData.PolicyHolderInfo &&
                caseDraftData.PolicyHolderInfo.CompanyName) ||
                '',
            EmailAddress = (caseDraftData &&
                caseDraftData.PolicyHolderInfo &&
                caseDraftData.PolicyHolderInfo.EmailAddress) ||
                '',
            PhoneNumber = (caseDraftData &&
                caseDraftData.PolicyHolderInfo &&
                caseDraftData.PolicyHolderInfo.PhoneNumber) ||
                '',
            CaseTypeId = (caseDraftData &&
                caseDraftData.PolicyHolderInfo &&
                caseDraftData.PolicyHolderInfo.CaseTypeId) ||
                '',
            CaseTypeName = (caseDraftData &&
                caseDraftData.PolicyHolderInfo &&
                caseDraftData.PolicyHolderInfo.CaseTypeName) ||
                null,
            Remarks = (caseDraftData && caseDraftData.PolicyHolderInfo && caseDraftData.PolicyHolderInfo.Remarks) || ''
        }: PolicyHolderDetails = new PolicyHolderDetails();
        return this.formBuilder.group({
            Name: [Name],
            CompanyName: [CompanyName],
            EmailAddress: [EmailAddress],
            PhoneNumber: [PhoneNumber,phoneValidator],
            CaseComponentName: [''],
            CaseTypeId: [CaseTypeId],
            CaseTypeName: [CaseTypeName],
            Remarks: [Remarks]
        });
    }

    createCaseReferenceForm(emailData, InsuranceCompanyName?, caseDraftData?: InsuranceCase) {
        // ;
        const {
            InsuranceComapnayId = (caseDraftData &&
                caseDraftData.CaseReferenceInfo &&
                caseDraftData.CaseReferenceInfo.InsuranceComapnayId) ||
                emailData.InsuranceCompanyId,
            InsuranceComapnayName = (caseDraftData &&
                caseDraftData.CaseReferenceInfo &&
                caseDraftData.CaseReferenceInfo.InsuranceComapnayName) ||
                InsuranceCompanyName,
            DepartmentId = (caseDraftData &&
                caseDraftData.CaseReferenceInfo &&
                caseDraftData.CaseReferenceInfo.DepartmentId) ||
                emailData.DepartmentId,
            DepartmentName = (caseDraftData &&
                caseDraftData.CaseReferenceInfo &&
                caseDraftData.CaseReferenceInfo.DepartmentName) ||
                emailData.DepartmentName
        }: CaseReference = new CaseReference();
        return this.formBuilder.group({
            InsuranceComapnayId: [InsuranceComapnayId, [Validators.required, Validators.maxLength(100)]],
            InsuranceComapnayName: [InsuranceComapnayName, [Validators.required, Validators.maxLength(100)]],
            DepartmentId: [DepartmentId, [Validators.required, Validators.maxLength(100)]],
            DepartmentName: [DepartmentName, [Validators.required, Validators.maxLength(100)]]
        });
    }

    createServiceForm(service?: ServiceModel, caseDraftData?: InsuranceCase) {
        const {
            ServiceName = null,
            Unit,
            Quantities,
            UnitPrice,
            TotalPrice,
            Vat,
            UpdatedOfferNumber,
            Remarks = null,
            ConstructionCompanyName = null,
            ConstructionCompanyEmail = null,
            ConstructionCompanyPhone = null
        } = service || new ServiceModel();
        return this.formBuilder.group({
            ServiceList: [ServiceName, [Validators.required]],
            Unit: [Unit],
            Quantity: [Quantities],
            UnitPrice: [UnitPrice],
            TotalPrice: [TotalPrice],
            VatSentence: [Vat],
            UpdatedOfferNumber: [UpdatedOfferNumber],
            Remarks: [Remarks],
            ConstructionCompanyName: [ConstructionCompanyName, [Validators.required, this.noWhitespaceValidator]],
            ConstructionCompanyEmail: [ConstructionCompanyEmail],
            ConstructionCompanyPhone: [ConstructionCompanyPhone,phoneValidator],
            ServiceAdditionalData: this.crateServiceAdditionalInfoForm(service)
        });
    }

    crateServiceAdditionalInfoForm(service?: any) {
        this.currentRole = this.commonService.getCurrentRoleUser();
        const {
            CaseTradeId = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.CaseTradeId) || '',
            CaseTradeName = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.CaseTradeName) ||
                null,
            CaseComponentId = (service &&
                service.ServiceAdditionalData &&
                service.ServiceAdditionalData.CaseComponentId) ||
                '',
            // NewPrice = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.NewPrice) || 0,
            // SavedCost = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.SavedCost) || 0,
            // Unit = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.Unit) || null,
            // Quantity = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.Quantity) || 0,
            // UnitPrice = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.UnitPrice) || 0,
            // TotalPrice = (service && service.ServiceAdditionalData && service.ServiceAdditionalData.TotalPrice) || 0,
            ConstructionCompanyName = (service &&
                service.ServiceAdditionalData &&
                service.ServiceAdditionalData.ConstructionCompanyName) ||
                '',
            ConstructionCompanyEmail = (service &&
                service.ServiceAdditionalData &&
                service.ServiceAdditionalData.ConstructionCompanyEmail) ||
                '',
            ConstructionCompanyPhone = (service &&
                service.ServiceAdditionalData &&
                service.ServiceAdditionalData.ConstructionCompanyPhone) ||
                ''
        }: ServiceAdditionalInfo = new ServiceAdditionalInfo();

        return this.formBuilder.group({
            CaseTradeId: [CaseTradeId],
            CaseTradeName: [CaseTradeName],
            CaseComponentId: [CaseComponentId],
            // NewPrice: [NewPrice],
            // SavedCost: [SavedCost],
            // Unit: [Unit],
            // Quantity: [Quantity],
            // UnitPrice: [UnitPrice],
            // TotalPrice: [TotalPrice],
            ConstructionCompanyName: [ConstructionCompanyName],
            ConstructionCompanyEmail: [ConstructionCompanyEmail],
            ConstructionCompanyPhone: [ConstructionCompanyPhone] //Do not add Validator
        });
    }

    getUserList(filter?) {
        const entityName = SCConfig.Entity.Person;
        const fields = SCConfig.UserReadableDatas.Person;
        const filters: Filter[] = [];
        const billOfEntryNumber = 'client_inspector';
        const primaryFilters = [];
        filters.push({
            property: 'Roles',
            operator: '__contains',
            value: `['client_inspector']`
        });
        if (filter) {
            filters.push({
                property: 'DisplayName',
                operator: 'search',
                value: filter
            });
        }

        primaryFilters.push({
            operator: '__and',
            value: [
                // {
                // 	propertyName: "Tags",
                // 	operator: "__contains",
                // 	value: `['Is-Transaction-Material']`,
                // },
                {
                    propertyName: 'Roles',
                    operator: '__contains',
                    value: `['${billOfEntryNumber}']`
                }
            ]
        });

        const generatedFilter = this.graphqlService.prepareFilterFromOperator(primaryFilters);
        return this.gqlQueryCommandService.gqlWatchQuery<any>(
            entityName,
            fields,
            generatedFilter,
            'LastUpdateDate:1',
            0,
            100
        );

        // .pipe(map(res  => (res && res.Data ? res.Data : [])));
    }

    public getInspectorUserList(
        searchKey: string | null = null,
        page: number = 0,
        pageSize: number = 100
    ): Observable<GqlWatchResponse<Person>> {
        const entityName = SCConfig.Entity.Person;
        const fields = SCConfig.UserReadableDatas.Person;
        const filters: Filter[] = [];
        filters.push({
            operator: 'raw',
            value: `$and: [ { Roles: { $in: ['client_inspector', 'admin'] } }, { Roles: { $nin: ['system_admin'] } } ]`
        });
        if (searchKey) {
            filters.push(new Filter('DisplayName', 'search', searchKey));
        }
        filters.push(new Filter('Active', '=', true));
        return this.gqlQueryCommandService.gqlWatchQuery<Person>(
            entityName,
            fields,
            filters,
            'DisplayName: 1',
            page,
            pageSize
        );
    }

    getInspectorUserListByEndpoint(
        searchKey: string | null = null,
        page: number = 0,
        pageSize: number = 100,
        usePagination: boolean = true
    ): Observable<GqlWatchResponse<Person>> {
        const payload = {
            PageNumber: page + 1, // API is 1-based index
            PageLimit: pageSize,
            OrderBy: 'DisplayName',
            SortOrder: 'Ascending',
            SearchKey: searchKey
                ? [
                      { Key: 'DisplayName', Value: searchKey },
                      { Key: 'Email', Value: searchKey }
                  ]
                : [],
            UploadIpexSiteId: SCConfig.UploadIpexSiteId,
            IncludeInactive: false,
            ExcludeRoles: ['system_admin'],
            IncludeRoles: ['client_inspector', 'admin'],
            Organization: '',
            Department: '',
            IsVerified: true,
            LastLoginFrom: null,
            LastLoginTo: null,
            UsePagination:usePagination
        };

        const url = `${this.urlResolver.getPcxQueryUrl()}GetPersonList`;
        return this.http.post(url, payload, SCConfig.HttpCommonOptions).pipe(
            map((response: any) => {
                const personsData = response.Data || [];
                const totalCount = response.TotalCount || 0;

                return new GqlWatchResponse<Person>(personsData, totalCount);
            })
        );
    }

    public getBackOfficeUserList(searchKey: string | null = null): Observable<GqlWatchResponse<Person>> {
        const entityName = SCConfig.Entity.Person;
        const fields = SCConfig.UserReadableDatas.Person;
        const filters: Filter[] = [];
        filters.push({
            operator: 'raw',
            value: `$and: [ { Roles: { $in: ['client_backoffice', 'admin'] } }, { Roles: { $nin: ['system_admin'] } } ]`
        });
        if (searchKey) {
            filters.push(new Filter('DisplayName', 'search', searchKey));
        }
        filters.push(new Filter('Active', '=', true));
        return this.gqlQueryCommandService.gqlWatchQuery<Person>(entityName, fields, filters, 'DisplayName: 1', 0, 100);
    }

    getInsuranceCompanyList(ItemId?): Observable<GqlWatchResponse<InsuranceCompanySeed>> {
        const entityName = SCConfig.Entity.InsuranceCompanySeed;
        const fields = SCConfig.UserReadableDatas.InsuranceCompanySeed;
        const filters: Filter[] = [];
        if (ItemId) {
            filters.push({
                operator: 'raw',
                value: `'_id': '${ItemId}'`
            });
        }
        return this.gqlQueryCommandService.gqlWatchQuery<InsuranceCompanySeed>(
            entityName,
            fields,
            filters,
            'LastUpdateDate:1'
        );

        // .pipe(map(res  => (res && res.Data ? res.Data : [])));
    }

    getDraftCaseIfExist(emailId: string) {
        const filter = [
            new Filter(null, 'raw', `'EmailConversationInfos.ItemId': {$in: ["${emailId}"]}`),
            new Filter('IsDraft', '=', true)
        ];
        const processCaseUrl = `${this.urlResolver.getPcxQueryUrl()}GetInsuranceCase`;

        const sortBy = '{CreateDate: -1}';

        const payload = {
            SortBy: sortBy,
            FilterString: `{${this.gqlQueryBuilderService.prepareFilters(filter)}}`,
            PageNumber: 0,
            PageSize: 10
        };

        return this.http
            .post(processCaseUrl, payload, { headers: this.header, withCredentials: true, observe: 'body' })
            .pipe(
                map((response: EcapQueryHandlerResponse<InsuranceCase>) => {
                    return response;
                })
            );
        // const entityName = SCConfig.Entity.InsuranceCase;
        // const fields = SCConfig.UserReadableDatas.InsuranceCase;
        // const filters: Filter[] = [];
        // if (emailId) {
        //     filters.push({
        //         operator: 'raw',
        //         value: `'EmailConversationId': '${emailId}'`
        //     });
        //     filters.push({
        //         operator: 'raw',
        //         value: `'IsDraft': true`
        //     });
        // }
        // return this.gqlQueryCommandService.gqlWatchQuery<any>(entityName, fields, filters, 'LastUpdateDate:1');
    }

    updateForm(caseDraftData, isInspector, initData) {
        const {
            CleimNumberInsurance = caseDraftData && caseDraftData.InsuranceClaimNumber,
            EmailConversationId = caseDraftData.EmailConversationId,
            InsuranceOfficerName = (caseDraftData && caseDraftData.InsuranceOfficerName) || '',
            InsuranceOfficerEmail = (caseDraftData && caseDraftData.InsuranceOfficerEmail) || '',
            OrderDate = (caseDraftData &&
                new Date(caseDraftData.OrderDate).getFullYear() >= 2000 &&
                caseDraftData.OrderDate) ||
                new Date(),
            IssuerZipCode = (caseDraftData && caseDraftData.ZipCodeOfIssuer) || '',
            PolicyHolderName,
            IssuerCompanyName,
            IssuerEmailAddress,
            IssuerPhoneNumber,
            CaseType,
            Remarks,
            ServiceName,
            Unit,
            Quantities,
            UnitPrice,
            TotalPrice,
            Vat,
            OfferNumber,
            Trade,
            Component,
            AssignedBackOffice,
            AssignedBackOfficeSearchText,
            AssignedInspector,
            AssignedInspectorSearchText,
            InvoiceType,
            CaseStatus = {
                Key: 'AUDIT_PHASE',
                Value: 'audit-phase'
            },
            BackofficeUserId = (caseDraftData && caseDraftData.BackofficeUserId) || '',
            InspectorUserId = (caseDraftData && caseDraftData.InspectorUserId) || ''
        }: BasicInformationModel = new BasicInformationModel();
        console.warn('Test ser', caseDraftData, initData);
        this.currentRole = this.commonService.getCurrentRoleUser();
        const ServiceList: ServiceModel[] = ((caseDraftData && caseDraftData.CaseServiceList) || []).map(
            service => new ServiceModel(service, this.currentRole)
        );
        if (!(caseDraftData && caseDraftData.CaseServiceList)) {
            ServiceList.push(new ServiceModel());
        }
        let inspectorQuestionData: any;

        if (caseDraftData && caseDraftData.InspectorAnswerList && caseDraftData.InspectorAnswerList.length > 0) {
            inspectorQuestionData = caseDraftData.InspectorAnswerList;
        } else {
            inspectorQuestionData = this.currentRole === SCConfig.CurrentRole.Admin ? [] : initData || [];
        }

        return this.formBuilder.group({
            CaseReferenceInfo: this.createCaseReferenceForm(caseDraftData, caseDraftData, caseDraftData),
            InsuranceClaimNumber: [CleimNumberInsurance, [Validators.required]],
            InsuranceOfficerName: [InsuranceOfficerName],
            InsuranceOfficerEmail: [InsuranceOfficerEmail],
            OrderDate: [OrderDate, [Validators.required, Validators.maxLength(100)]],
            ZipCodeOfIssuer: [IssuerZipCode, [Validators.required, Validators.maxLength(25)]],
            BackofficeUserId: [BackofficeUserId, [Validators.required, Validators.maxLength(100)]],
            InspectorUserId: [InspectorUserId],
            InvoiceType: [InvoiceType],
            AssignedBackOfficeSearchText: [AssignedBackOfficeSearchText],
            AssignedInspectorSearchText: [AssignedInspectorSearchText],
            CaseStatus: [CaseStatus],
            EmailConversationId: [EmailConversationId],
            CaseServiceList: this.formBuilder.array(ServiceList.map(c => this.createServiceForm(c, caseDraftData))),
            PolicyHolderInfo: this.createPolicyHolderDetails(caseDraftData, caseDraftData),
            InspectorAnswerList: this.formBuilder.array(inspectorQuestionData.map(data => this.createFormGroup(data)))
        });
    }

    inspectorInitForm(questionListData) {
        return this.formBuilder.group({
            InspectorAnswerList: this.formBuilder.array(questionListData.map(data => this.createFormGroup(data)))
        });
    }

    sectionList(c) {
        const formControlName = c.Title;
        return this.formBuilder.group({
            [formControlName]: []
        });
    }

    createFormGroup(question: any) {
        const {
            AnswerId = question.AnswerId || this.utilityService.getNewGuid(),
            QuestionId = question.QuestionId,
            SectionId = question.SectionId || '',
            SelectedOptionId = question.SelectedOptionId || '',
            Remarks = question.Remarks || ''
        }: InspectorAnswer = new InspectorAnswer();
        return this.formBuilder.group({
            AnswerId: [AnswerId],
            QuestionId: [QuestionId],
            SectionId: [SectionId],
            SelectedOptionId: [SelectedOptionId],
            Remarks: [Remarks]
        });
    }

    getInspectorQuestionSeedList() {
        const entityName = SCConfig.Entity.InspectorQuestionSeed;
        const fields = SCConfig.UserReadableDatas.InspectorQuestionSeed;
        const filters: Filter[] = [];
        return this.gqlQueryCommandService.gqlWatchQuery<any>(entityName, fields, filters, 'CreateDate:1');
    }

    getUserByCreateBy(ItemId) {
        const entityName = SCConfig.Entity.Person;
        const fields = SCConfig.UserReadableDatas.Person;
        const filters: Filter[] = [];

        filters.push(new Filter('CreatedBy', '=', ItemId));
        return this.gqlQueryCommandService.gqlWatchQuery<Person>(entityName, fields, filters);
    }
    noWhitespaceValidator(control: FormControl) {
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { required: true };
    }

    // pushes component seed new translation mapping to the translation service like >> itemId >> Label.Value
    // didn't have enough time to move this to a common place and then do the operation, F&F_Tokyo_Drift_FTW
    pushComponentSeedLabelsToTranslation(componentSeeds: ComponentSeed[], translateService: TranslateService) {
        if (componentSeeds === null || !componentSeeds) {
            return;
        }
        componentSeeds.forEach(componentSeed => {
            let labels = componentSeed.Label;
            labels.forEach(label => {
                let tempDictionary = {};
                tempDictionary[componentSeed.ItemId] = label.Value;
                translateService.setTranslation(CommonService.getLangFromLanguageKey(label.Key), tempDictionary, true);
            });
        });
    }
}
